import { useState, useEffect, useContext, useRef } from 'react';
import { UserContext } from '../../../auth/auth0-provider-with-history';
import StepIndicator from './steps/StepIndicator';
import StepOne from './steps/StepOne';
import StepTwo from './steps/StepTwo';
import StepThree from './steps/StepThree';
import StepFour from './steps/StepFour';
import StepFive from './steps/StepFive';
import ServiceChangeWarningModal from './modals/ServiceChangeWarningModal';
import PrivacyStatementModal from './modals/PrivacyStatementModal';
import { 
    useServiceSelection, 
    useProviderCheck, 
    useProjectForm, 
    useQuestionAnswers, 
    useFileUpload, 
    useAnalytics 
} from './hooks';
import { projectService } from './services/projectService';

const ProjectSteps = (props) => {
    const [activeTab, setActiveTab] = useState(1);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);
    const [createProjectDisabled, setCreateProjectDisabled] = useState(false);
    const errorDivRef = useRef(null);
    const { userData } = useContext(UserContext);

    // Custom hooks
    const { serviceState, serviceChangeWarning, updateCategory, updateSubCategory, updateService, acceptServiceChange, rejectServiceChange, setServiceCategories } = useServiceSelection(
        props.subcatId ? 0 : 0, 
        props.subcatId || 0, 
        props.serviceId || 0
    );

    const { providerState, getPostalCode, checkProvider, updatePostalCode, updateSelectedArea, resetProviderCheck, setProviderState } = useProviderCheck();
    
    const { formData, errors, updateField, updateFieldWithValidation, validateAll, resetForm, setFormData } = useProjectForm();
    
    const { answers, answersComplete, handleAnswer, resetAnswers } = useQuestionAnswers(
        serviceState.serviceCategories,
        serviceState.selectedCatId,
        serviceState.selectedSubcatId,
        serviceState.selectedServiceId
    );
    
    const { uploadedFiles, handleDrop, removeFile, clearFiles } = useFileUpload(3);
    
    const { trackStepView, trackProjectCreation } = useAnalytics();

    // Legacy state object for backward compatibility with child components
    const state = {
        activeTab,
        serviceCategories: serviceState.serviceCategories,
        selectedCatId: serviceState.selectedCatId,
        selectedSubcatId: serviceState.selectedSubcatId,
        selectedServiceId: serviceState.selectedServiceId,
        checkPostalCode: providerState.checkPostalCode,
        availablePostalAreas: providerState.availablePostalAreas,
        selectedPostalAreaId: providerState.selectedPostalAreaId,
        checkProvider: providerState.checkProvider,
        checkProviderResult: providerState.checkProviderResult,
        showPostCodeError: providerState.showPostCodeError,
        customerDescription: formData.customerDescription,
        customerUploadedFiles: uploadedFiles,
        customerLastName: formData.customerLastName,
        customerFirstName: formData.customerFirstName,
        customerGsmNumber: formData.customerGsmNumber,
        customerEmail: formData.customerEmail,
        customerAddressStreetAndHouse: formData.customerAddressStreetAndHouse,
        customerAddressBusStation: formData.customerAddressBusStation,
        customerAddressCity: formData.customerAddressCity,
        customerPrivacyAccepted: formData.customerPrivacyAccepted,
        customerPrivacyMarketing: formData.customerPrivacyMarketing,
        customerIsAxaCustomer: formData.customerIsAxaCustomer,
        errorGsm: errors.gsm,
        errorLastName: errors.lastName,
        errorFirstName: errors.firstName,
        errorEmail: errors.email,
        errorPrivacy: errors.privacy,
        errorCustomerAddressStreetAndHouse: errors.address,
        errorCustomerIsAxaCustomer: errors.isAxaCustomer,
        answers,
        answersComplete,
        createProjectDisabled,
        showErrorDiv: Object.values(errors).some(error => error),
        postalArea: providerState.postalArea,
    };

    // Legacy setState function for backward compatibility
    const setState = (updater) => {
        if (typeof updater === 'function') {
            const newState = updater(state);
            applyStateUpdates(newState);
        } else {
            applyStateUpdates(updater);
        }
    };

    const applyStateUpdates = (newState) => {
        if (newState.activeTab !== undefined) setActiveTab(newState.activeTab);
        if (newState.checkPostalCode !== undefined) updatePostalCode(newState.checkPostalCode);
        if (newState.selectedPostalAreaId !== undefined) updateSelectedArea(newState.selectedPostalAreaId);
        if (newState.customerDescription !== undefined) updateField('customerDescription', newState.customerDescription);
        if (newState.customerIsAxaCustomer !== undefined) updateField('customerIsAxaCustomer', newState.customerIsAxaCustomer);
        if (newState.customerPrivacyAccepted !== undefined) updateField('customerPrivacyAccepted', newState.customerPrivacyAccepted);
        if (newState.customerPrivacyMarketing !== undefined) updateField('customerPrivacyMarketing', newState.customerPrivacyMarketing);
        if (newState.privacyStatementModalShown !== undefined) setShowPrivacyModal(newState.privacyStatementModalShown);
        if (newState.customerAddressStreetAndHouse !== undefined) {
            updateField('customerAddressStreetAndHouse', newState.customerAddressStreetAndHouse);
        }
        if (newState.customerAddressBusStation !== undefined) {
            updateField('customerAddressBusStation', newState.customerAddressBusStation);
        }
    };

    const getServices = async () => {
        try {
            const categories = await projectService.getAllActiveServiceCategories();
            setServiceCategories(categories);
            
            if (props.subcatId) {
                const parentCategory = categories.find(cat => 
                    cat.serviceSubCategories.some(sub => sub.id === parseInt(props.subcatId))
                );
                if (parentCategory) {
                    updateCategory(parentCategory.id, true);
                    updateSubCategory(parseInt(props.subcatId), true);
                }
            }
            
            if (props.serviceId) {
                updateService(parseInt(props.serviceId), true);
            }
        } catch (error) {
            console.error('Failed to load services:', error);
        }
    };

    const handleServiceCatUpdate = (catId) => {
        updateCategory(catId);
    };

    const handleServiceSubCatUpdate = (subCatId) => {
        updateSubCategory(subCatId);
    };

    const handleServiceUpdate = (serviceId) => {
        updateService(serviceId);
    };

    const handleAcceptServiceChange = () => {
        acceptServiceChange(() => {
            resetProviderCheck();
            resetAnswers();
        });
    };

    const handleGetPostCode = async () => {
        await getPostalCode(providerState.checkPostalCode);
    };

    const handleGetProvider = async () => {
        const provider = await checkProvider(serviceState.selectedServiceId, providerState.selectedPostalAreaId);
        
        if (provider) {
            const selectedArea = providerState.availablePostalAreas.find(
                x => x.id === parseInt(providerState.selectedPostalAreaId)
            );
            updateField('customerAddressCity', selectedArea?.cityPostalArea || '');
        }
    };

    const handleGSMInput = (event) => {
        updateField('customerGsmNumber', event.target.value);
    };

    const handleGSMBlur = (event) => {
        updateFieldWithValidation('customerGsmNumber', event.target.value, 'gsm');
    };

    const handleLastNameInput = (event) => {
        updateField('customerLastName', event.target.value);
    };

    const handleLastNameBlur = (event) => {
        updateFieldWithValidation('customerLastName', event.target.value, 'lastName');
    };

    const handleFirstNameInput = (event) => {
        updateField('customerFirstName', event.target.value);
    };

    const handleFirstNameBlur = (event) => {
        updateFieldWithValidation('customerFirstName', event.target.value, 'firstName');
    };

    const handleEmailInput = (event) => {
        updateField('customerEmail', event.target.value);
    };

    const handleEmailBlur = (event) => {
        updateFieldWithValidation('customerEmail', event.target.value, 'email');
    };

    const createProject = async () => {
        const isValid = validateAll();
        
        if (!isValid || !formData.customerAddressCity) {
            setCreateProjectDisabled(false);
            return;
        }

        setCreateProjectDisabled(true);

        try {
            const projectRequest = projectService.buildProjectRequest(
                serviceState,
                providerState,
                formData,
                answers,
                userData.language
            );

            const response = await projectService.createProject(projectRequest);

            if (!response.isError && response.data) {
                await projectService.uploadProjectFiles(
                    response.data.projectId,
                    response.data.verificationId,
                    uploadedFiles
                );
                
                setActiveTab(5);
                props.showSuccessMessage();
                trackProjectCreation(formData.customerIsAxaCustomer, formData.customerPrivacyMarketing);
            } else {
                // Reset on error
                resetAll();
            }
        } catch (error) {
            console.error('Failed to create project:', error);
            resetAll();
        } finally {
            setCreateProjectDisabled(false);
        }
    };

    const resetAll = () => {
        setActiveTab(1);
        resetForm();
        resetProviderCheck();
        resetAnswers();
        clearFiles();
    };

    const moveToStep = (stepNumber, stepName, additionalData = {}) => {
        setActiveTab(stepNumber);
        trackStepView(stepNumber, stepName, additionalData);
    };

    const moveToStepTwo = () => {
        const selectedSubcat = serviceState.serviceCategories
            ?.find(x => x.id === parseInt(serviceState.selectedCatId))
            ?.serviceSubCategories?.find(x => x.id === parseInt(serviceState.selectedSubcatId));
        
        const selectedService = selectedSubcat?.services?.find(
            x => x.id === parseInt(serviceState.selectedServiceId)
        );

        moveToStep(2, "pro finder", {
            "service sub category": selectedSubcat?.name?.toLowerCase(),
            "service": selectedService?.name?.toLowerCase(),
        });
    };

    const moveToStepThree = () => {
        const postalCode = providerState.availablePostalAreas.find(
            x => x.id === parseInt(providerState.selectedPostalAreaId)
        )?.postCode || '';
        
        moveToStep(3, "intervention detail", {
            "postal code": postalCode
        });
    };

    const moveToStepFour = () => {
        moveToStep(4, "personal data");
    };

    const initialStep = () => {
        moveToStep(1, "service selection");
    };

    // Scroll to error div when errors appear
    useEffect(() => {
        if (Object.values(errors).some(error => error) && errorDivRef.current) {
            errorDivRef.current.scrollIntoView({
                behavior: 'auto',
                block: 'center',
                inline: 'center'
            });
        }
    }, [errors]);

    // Scroll to top on step change
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [activeTab]);

    // Load services on mount
    useEffect(() => {
        getServices();
        initialStep();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <StepIndicator activeTab={activeTab} userData={userData} />
            
            <div align="center" >
                <div className="tab-content">
                    <div className={"tab-pane fade " + (activeTab === 1 ? "show active" : "")} tabIndex="0">
                        <StepOne
                            state={state}
                            userData={userData}
                            updateServiceCat={handleServiceCatUpdate}
                            updateServiceSubCat={handleServiceSubCatUpdate}
                            updateService={handleServiceUpdate}
                            onNext={moveToStepTwo}
                        />
                    </div>
                    <div className={"tab-pane fade " + (activeTab === 2 ? "show active" : "")} tabIndex="0" >
                        <StepTwo
                            state={state}
                            userData={userData}
                            setState={setState}
                            getPostCode={handleGetPostCode}
                            getProvider={handleGetProvider}
                            onNext={moveToStepThree}
                            onBack={initialStep}
                        />
                    </div>
                    <div className={"tab-pane fade " + (activeTab === 3 ? "show active" : "")} tabIndex="0">
                        <StepThree
                            state={state}
                            userData={userData}
                            setState={setState}
                            handleAnswer={handleAnswer}
                            onDrop={handleDrop}
                            onRemoveFile={removeFile}
                            onNext={moveToStepFour}
                            onBack={() => setActiveTab(2)}
                        />
                    </div>
                    <div className={"tab-pane fade " + (activeTab === 4 ? "show active" : "")} tabIndex="0">
                        <StepFour
                            state={state}
                            userData={userData}
                            setState={setState}
                            handleGSMInput={handleGSMInput}
                            handleGSMBlur={handleGSMBlur}
                            handleLastNameInput={handleLastNameInput}
                            handleLastNameBlur={handleLastNameBlur}
                            handleFirstNameInput={handleFirstNameInput}
                            handleFirstNameBlur={handleFirstNameBlur}
                            handleEmailInput={handleEmailInput}
                            handleEmailBlur={handleEmailBlur}
                            createProject={createProject}
                            errorDivRef={errorDivRef}
                            onBack={() => setActiveTab(3)}
                            onPrivacyClick={() => setShowPrivacyModal(true)}
                        />
                    </div>
                    <div className={"tab-pane fade " + (activeTab === 5 ? "show active" : "")} tabIndex="0">
                        <StepFive
                            state={state}
                            userData={userData}
                        />
                    </div>
                </div>
            </div>

            <ServiceChangeWarningModal
                shown={serviceChangeWarning.shown}
                userData={userData}
                onAccept={handleAcceptServiceChange}
                onReject={rejectServiceChange}
            />

            <PrivacyStatementModal
                shown={showPrivacyModal}
                userData={userData}
                onClose={() => setShowPrivacyModal(false)}
            />
        </>
    );
};

export default ProjectSteps;